/**
 * Generation as a REVISION of what a person already has.
 *
 * Decision [`0045`](../../../Agents/Decisions/0045-accepted-revises-pinned-and-novelty-withdraw.md)
 * kept `GenerationRequest.accepted` and made it a contract:
 *
 * > Absence of an instruction means preservation; it does not mean "return to
 * > baseline." A pool prior, manual taste correction, or ordinary candidate
 * > therefore cannot erase a drop, placement, module configuration, or
 * > unrelated accepted choice while changing density or palette.
 *
 * > It must not silently discard that structure and call a fresh baseline a
 * > revision.
 *
 * Until this module the field was read by nothing: every caller supplied it and
 * every caller got a fresh tree. `0121` records that, and records why fixing it
 * in the one path @Codex found it in would have been worse than leaving it —
 * it would have made the field look like it works.
 *
 * ## The ownership map, and why it is not a field list
 *
 * @Codex's interpretation of `0045` control 1, ruled 2026-08-30 and recorded in
 * `0121`:
 *
 * > **The exclusion set is derived from the request's effective, live
 * > instructions — not a fixed field list.** However, it must **not** be
 * > derived mechanically from every property present in the request.
 *
 * Both of the readings I offered were wrong. A fixed list *"would drift as real
 * instructions are added"*; deriving exclusions from request keys means *"a
 * decorative or unknown field could excuse an unrelated redesign"* — which is
 * exactly the failure `0121` is a task about.
 *
 * So `OWNERSHIP` below is closed and **dynamic by accepted capability**. A
 * field entering `TastePriors` does not widen it. It widens only when the
 * change that makes the field live also names the canonical paths it governs
 * and adds the compiled-layout control that proves changing it changes those
 * paths — which is the same bar `0036` sets for a knob existing at all.
 */

import { ROLE_KIND, isVisualKind, validateSpec } from '@morph/spec';
import type { TLayoutSpec, TNode, Role } from '@morph/spec';
import type { GenerationRequest, TastePriors } from './types.js';

/**
 * A live instruction, and the canonical paths it may govern.
 *
 * `paths` are top-level spec keys this instruction owns outright. `nodeLocal`
 * says the instruction also has consequences inside `root` — and those are
 * deliberately NOT expressed as paths, because a node-local consequence is
 * defined by the role it names rather than by where that role happens to sit.
 * `0045` says so in the same breath: *"the node-local consequences of
 * `suppressedRoles` and `prominentRoles`"*.
 */
interface Ownership {
  readonly paths: readonly (keyof TLayoutSpec)[];
  readonly nodeLocal: boolean;
}

export const OWNERSHIP: Readonly<Record<string, Ownership>> = {
  density: { paths: ['density'], nodeLocal: false },
  typography: { paths: ['typography'], nodeLocal: false },
  palette: { paths: ['palette'], nodeLocal: false },
  suppressedRoles: { paths: [], nodeLocal: true },
  prominentRoles: { paths: [], nodeLocal: true },
};

/**
 * Which instructions this request actually carries.
 *
 * "Effective and live", not "present": an empty `suppressedRoles` instructs
 * nothing, and treating it as live would let a caller widen the exclusion set
 * by passing `[]`.
 */
export function effectiveInstructions(taste: TastePriors | undefined): readonly string[] {
  if (!taste) return [];
  const live: string[] = [];
  for (const name of Object.keys(OWNERSHIP)) {
    const value = (taste as unknown as Record<string, unknown>)[name];
    if (value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    live.push(name);
  }
  return live;
}

/** The top-level paths the live instructions of this request govern. */
export function governedPaths(taste: TastePriors | undefined): readonly string[] {
  return effectiveInstructions(taste).flatMap((name) => [...OWNERSHIP[name]!.paths]);
}

/**
 * The refusal `0045` requires instead of a fresh baseline.
 *
 * > A fresh baseline may be useful, but presenting it as a revision hides the
 * > loss of a person's arrangement. The caller may explicitly make a new
 * > generation request without `accepted` after showing the refusal; the
 * > generator may not choose that loss on the caller's behalf.
 *
 * Its own class rather than an `Error` with a recognisable message, because
 * `compile` has to tell it apart from a generator that simply broke: a broken
 * generator falls through to the fallback, and this one must NOT — falling back
 * is the loss the decision rejects, arriving through the pipeline instead of
 * through the generator.
 */
export class AcceptedNoLongerLegal extends Error {
  readonly problems: readonly string[];
  /**
   * WHICH boundary refused, which `0049` requires be distinguishable.
   *
   * > The two occasions must be distinguishable in diagnostics as
   * > `accepted-input` and `revised-output`, but callers may present both as an
   * > accepted arrangement that Morph cannot safely revise.
   *
   * So it is a field rather than a second class: the public refusal stays one
   * thing — `compile` tells it from a broken generator by class, and every
   * caller that only wants to say "Morph cannot safely revise this" needs no
   * new branch — while a diagnostic that wants to say WHERE can ask.
   *
   * `undefined` on the revision-specific refusals (`illegalities`, an emptied
   * root, a suppressed role a revision cannot remove): those are neither
   * boundary, and labelling them one would be the third defect this file has
   * had from a name that describes something the code does not do.
   */
  readonly boundary: 'accepted-input' | 'revised-output' | undefined;
  constructor(problems: readonly string[], boundary?: 'accepted-input' | 'revised-output') {
    super(`the accepted arrangement is no longer legal for this shape: ${problems.join('; ')}`);
    this.name = 'AcceptedNoLongerLegal';
    this.problems = problems;
    this.boundary = boundary;
  }
}

/**
 * The ordinary validator, at a trust boundary, reported without its messages.
 *
 * `0049` is explicit about all three halves of that sentence.
 *
 * > Both checks use the ordinary complete layout-spec validator, including
 * > registry checks. There is no revision-only structural subset that ignores
 * > capability registration, versions, or config.
 *
 * — because a structural subset would be *"a second definition of 'accepted'"*
 * and would make preservation depend on which door read the spec. And:
 *
 * > Its problems are bounded and contain validator code and path, not
 * > author-controlled validator messages.
 *
 * A validator message interpolates the spec's own strings — a capability name,
 * a config key — and an accepted spec is a document a person's content reached.
 * Code and path are ours; the message is not. `MAX` bounds the list because
 * `validateSpec` bounds its own at 200, which is bounded and is not a
 * diagnostic.
 */
const MAX_REPORTED = 10;
function validatorProblems(
  spec: TLayoutSpec,
  boundary: 'accepted-input' | 'revised-output',
): readonly string[] {
  const { valid, problems } = validateSpec(spec);
  if (valid) return [];
  /**
   * RANK AND DEDUPLICATE BEFORE CAPPING — @Codex, P2 on `2ffce98`. Ajv's
   * `oneOf` cascade reports every branch it tried: one invalid query
   * operator yields 46 problems, 45 of them naming `/root`, and a cap taken
   * in arrival order kept ten generic container failures while dropping the
   * one `/root/query/filter/where/op` line that named the cause. The
   * deepest path is the most specific claim, so it goes first; duplicates
   * of one (path, code) pair say nothing twice.
   */
  const seen = new Set<string>();
  const ordered = problems
    .filter((p) => {
      const k = `${p.path}\u0000${p.code}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => (b.path?.split('/').length ?? 0) - (a.path?.split('/').length ?? 0));
  const shown = ordered.slice(0, MAX_REPORTED)
    .map((p) => `${boundary} ${p.path || '/'}: ${p.code}`);
  return ordered.length > MAX_REPORTED
    ? [...shown, `${boundary} /: ${ordered.length - MAX_REPORTED} further problems`]
    : shown;
}

/**
 * Every key in the schema whose value is a ROLE.
 *
 * @Codex, P2 on `be5b9ec`: *"When an accepted collection groups by a role that
 * the new `ObjectShape` no longer carries, `groupBy` and `subGroupBy` are not
 * collected here even though both are `RoleRef` fields in `Query`.
 * Consequently `illegalities()` does not refuse the revision … so generation
 * successfully returns a lens querying a nonexistent role instead of the
 * required `AcceptedNoLongerLegal` refusal."*
 *
 * The comment this replaces said *"`role`, `labelRole`, `via` and `has` are the
 * four ways the schema names a role"* and told the reader to grep for `RoleRef`
 * to check. Grepping gives SEVEN. Being confidently specific about a list
 * somebody could verify, and being wrong, is worse than not listing it.
 *
 * So it is a named constant with a clause behind it: `revise.test.ts` reads
 * `schema.ts` for every `RoleRef` site and fails if this set does not cover
 * them. A future eighth arrives as a red test rather than as a lens querying a
 * role the shape does not have — which is `0134`'s lesson, applied one file
 * over on the day it was learned.
 */
export const ROLE_KEYS: ReadonlySet<string> =
  new Set(['role', 'labelRole', 'via', 'has', 'lookup', 'groupBy', 'subGroupBy']);

/** Every role the spec references, with the path that references it. */
export function roleReferences(spec: TLayoutSpec): ReadonlyArray<{ role: Role; path: string }> {
  const found: Array<{ role: Role; path: string }> = [];
  const walk = (value: unknown, path: string): void => {
    if (Array.isArray(value)) {
      value.forEach((v, i) => walk(v, `${path}/${i}`));
      return;
    }
    if (value === null || typeof value !== 'object') return;
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      /**
       * `config` is DESCRIPTOR-OWNED and is not walked.
       *
       * @Codex, P2 on `1c4d8b7`: *"When a runtime-registered module legally
       * declares a config key named `role`, such as `configShapes: { role: {
       * type: 'enum', values: ['compact', 'wide'] } }`, an accepted capability
       * carrying `config: { role: 'compact' }` reaches this generic key walk.
       * The value `compact` is then misclassified as a `RoleRef`, so
       * `illegalities()` reports that the current shape lacks it and refuses
       * every later density, palette, or pool revision even though the module
       * configuration is valid and decision 0045 requires it to be preserved."*
       *
       * The exact inverse of the finding one round earlier, which was that the
       * walk missed three real `RoleRef` sites. A key-name walk is wrong in
       * both directions: it can miss a role and it can invent one, and the
       * second is worse here because it refuses rather than admits.
       *
       * `config`'s keys and values belong to a module's own `configShapes`, and
       * `pool.ts` rebuilds them against that descriptor for the same reason —
       * they are a vocabulary the spec does not own. A capability's `channels`
       * ARE walked: a channel binding carries a real `role`, and a suppressed
       * role bound there is exactly the case the previous finding was about.
       */
      if (k === 'config') continue;
      if (ROLE_KEYS.has(k) && typeof v === 'string') {
        found.push({ role: v as Role, path: `${path}/${k}` });
      } else walk(v, `${path}/${k}`);
    }
  };
  walk(spec.root, '/root');
  if (spec.viewState) walk(spec.viewState, '/viewState');
  return found;
}

/**
 * Is the accepted arrangement still legal for the shape being generated for?
 *
 * The only illegality this can decide today is a role the shape no longer has.
 * A spec that binds it would render an empty field on every object with no way
 * to fill one — and `0045` is explicit that the generator may not quietly
 * replace such a spec with a fresh design.
 *
 * Suppressed roles are exempt: the revision is about to remove them, so a
 * binding to one is not a reason to refuse the whole arrangement.
 */
function illegalities(accepted: TLayoutSpec, req: GenerationRequest): readonly string[] {
  // Declared-but-empty counts as PRESENT — see `declaredRoles` in types.ts:
  // the shape counts populated values, and a role whose last value was just
  // cleared is still addressable through its manifest key and its empty
  // editable field. Only a role that is genuinely gone refuses the revision.
  const present = new Set([
    ...req.shape.roles.map((r) => r.role),
    ...(req.declaredRoles ?? []),
  ]);
  const suppressed = new Set(req.taste?.suppressedRoles ?? []);
  const problems: string[] = [];
  /**
   * CARDINALITY first, because it is the illegality that does not name a role.
   *
   * A spec built for one object draws a single card; a spec built for a set
   * draws a collection. Handing either to the other shape produces a lens that
   * validates and shows the wrong thing — and the demo reaches it directly,
   * because `state.lens.accepted` survives the cardinality switch that changed
   * the scope under it.
   *
   * This paragraph replaces a comment claiming the check was "refused above"
   * when nothing refused it. A comment that describes a guard the code does not
   * have is worse than no comment: it answers the question a reader came to
   * ask, wrongly.
   */
  if (accepted.target?.cardinality !== req.shape.cardinality) {
    problems.push(`/target/cardinality: the arrangement was built for `
      + `"${accepted.target?.cardinality}" and this shape is `
      + `"${req.shape.cardinality}"`);
  }
  /**
   * And CONTEXT, for the same reason and at the same place.
   *
   * @Codex, P2 on #184: *"When an accepted lens targets one context, such as
   * `browsing`, and a later pool request uses another context with the same
   * cardinality and roles, this compatibility check passes and
   * `reviseAccepted()` returns the preserved spec with its old
   * `target.context`. `validateSpec()` cannot catch that mismatch because it
   * does not receive the request, so the reading surface can be offered a
   * candidate still declaring itself as a browsing layout."*
   *
   * The cardinality paragraph above is this one's own argument arriving a
   * field later: `target` has three members, the request names two of them,
   * and only one was being compared. `mediaWeight` is deliberately NOT here —
   * it is a property of the shape's content rather than of the surface asking,
   * and `0045` withdraws on incompatibility rather than on difference.
   *
   * Refused rather than rewritten. Stamping `req.context` onto the preserved
   * spec would be the *"quietly replace"* `0045` forbids wearing the opposite
   * mask: the arrangement was designed for reading and nothing here knows what
   * it should look like for browsing.
   */
  if (accepted.target?.context !== req.context) {
    problems.push(`/target/context: the arrangement was built for `
      + `"${accepted.target?.context}" and this request is for `
      + `"${req.context}"`);
  }
  const seen = new Set<string>();
  for (const { role, path } of roleReferences(accepted)) {
    if (present.has(role) || suppressed.has(role) || seen.has(role)) continue;
    seen.add(role);
    problems.push(`${path}: the shape no longer carries the role "${role}"`);
  }
  return problems;
}

/**
 * The node-local half: remove suppressed roles, lead with prominent ones.
 *
 * Structural, and deliberately conservative — it deletes the binding or field
 * that names a suppressed role and nothing around it. A card that loses one of
 * four bindings keeps its id, its placement, its affordances and its other
 * three, which is what *"absence of an instruction means preservation"* means
 * one level down.
 *
 * A node that would be left with NOTHING is removed rather than emitted empty:
 * a card with no bindings and no children draws a box, and a person who hid its
 * only field asked for the field to be gone, not for a rectangle where it was.
 */
function applyNodeLocal(node: TNode, taste: TastePriors | undefined): TNode | undefined {
  const suppressed = new Set(taste?.suppressedRoles ?? []);
  const prominent = taste?.prominentRoles ?? [];
  const lead = (roles: readonly Role[]): number[] =>
    roles.map((r) => {
      const at = prominent.indexOf(r);
      return at === -1 ? prominent.length : at;
    });

  /**
   * A stable sibling ranking, used by BOTH the `children` array and every
   * capability slot array.
   *
   * @Codex, P2 on `9bd1b63`: *"When a legal capability slot contains sibling
   * fields such as `author` followed by `title`, promoting `title` still
   * returns the siblings in their original order: these recursive calls leave
   * field nodes unchanged, and unlike the `children` branch, this slot array is
   * never ranked. The revision therefore validates and reports success while
   * the promoted role does not move."*
   *
   * Right, and it is the SAME finding as the round before, one clause short:
   * that one added the slot WALK, so suppression inside a slot works; this one
   * is the other half of what `applyNodeLocal` does to a sibling list. The
   * previous fix's own comment names the failure it then reproduced —
   * *"a promotion that leaves a slot untouched returns success and an unchanged
   * lens, so the person's gesture went nowhere with nothing to notice."*
   *
   * A function rather than a second copy of the arithmetic, for the reason the
   * children block already gives about `lead`: two rankings that can disagree
   * about what a rank is are worse than one that is wrong.
   */
  /**
   * `headingFirst` — a region whose `purpose` is `editor` draws ONE OBJECT,
   * and its title is that page's heading rather than a field competing with
   * the others. The renderer already says so from the other side: it wraps
   * exactly this title in an `h1`, gated on the same declared purpose.
   *
   * Everywhere else the precedence is `TastePriors`' own — *"media and
   * identity still lead because they make the object recognisable"* — with
   * visual roles ahead of the name, which is right for a CARD, where the
   * picture is what you recognise the object by before you read it. On a
   * page you are already at the object; the heading tells you which one, and
   * a still above it is a cover, which is a different decision from a
   * promotion.
   */
  const byProminence = (nodes: TNode[], headingFirst = false): TNode[] => {
    // The array ITSELF when there is nothing to rank, not a copy of it. This
    // runs per node on every repaint, and the overwhelmingly common request
    // names no prominent role at all — a copy there is an allocation for a
    // sort that is not going to happen. `CLAUDE.md`'s regression question,
    // asked of the refactor that made this shared rather than of the fix.
    if (!prominent.length) return nodes;
    // Only a node that names ONE role of its own is ranked: `field` and `media`
    // through `binding.role`, `node-slot` through `via`. A card holds several
    // and a region holds none, so both sit at `prominent.length` and keep their
    // relative order — the restraint the children block states at length.
    /**
     * IDENTITY AND MEDIA STAY AHEAD — @Codex, P2 on `2ffce98`, and it is
     * `TastePriors`' own sentence: *"Media and identity still lead because
     * they make the object recognisable."* Fresh generation holds that
     * precedence; this ranking assigned the promoted role 0 and everything
     * else the fallback, so revising `[title, date]` with a promoted date
     * put Date ahead of the object's name. Promotion moves a role among
     * ORDINARY metadata: visual roles rank first, title and subtitle next,
     * and only what remains competes on `prominent`.
     */
    const roleOf = (c: TNode): Role | undefined => {
      const child = c as unknown as Record<string, unknown>;
      const k = child.kind as string;
      return k === 'field' || k === 'media'
        ? (child.binding as { role?: Role } | undefined)?.role
        : k === 'node-slot' ? child.via as Role | undefined
          : undefined;
    };
    // ONLY a sibling list a promotion actually reaches is re-ranked at all —
    // the preservation contract's own control ("a slot nobody promoted into
    // keeps the order it had") caught the first version of the tiering below
    // reordering untouched slots, which is the churn 0045 forbids.
    if (!nodes.some((c) => {
      const role = roleOf(c);
      return role !== undefined && prominent.includes(role);
    })) return nodes;
    /**
     * A CAPABILITY IS FIXED CHROME AND DOES NOT COMPETE — @Codex's P2 on
     * `ed4f7a7`.
     *
     * A roleless node takes `prominent.length`, a large positive, while
     * visual roles take -2 and title/subtitle -1. That is right for a card
     * or a region, which are content with no single rank. It is wrong for a
     * capability: the object pages put `workspace-pagehead` in the same
     * sibling list as the title, the attachments and the body, so promoting
     * `body` — or merely having a still on the page — ranked content ahead of
     * the page head, `templateShortfalls` found the head no longer first, and
     * the revision was REFUSED. Promote failed for a reason that had nothing
     * to do with what the person promoted.
     *
     * A module is not a thing a person can promote past; it is the frame the
     * content sits in. So the frame holds its index and the content is
     * ranked into the positions left over — which keeps the topology a
     * template's shape requires while still reordering everything the
     * promotion is actually about.
     */
    /**
     * A CONTAINER HOLDS ITS PLACE — and the same is true of a region, which
     * is the half the comment above already meant and did not achieve.
     *
     * It says a region *"sits at `prominent.length` and keeps its relative
     * order"*. Relative to other regions, yes. Relative to a LEAF, no: a
     * promoted field ranks at 0 and a region at `prominent.length`, so any
     * promotion anywhere pulls its field above every region on the page. On
     * an object page that put the body above the properties, which is a
     * topology the look fixes — and `templateShortfalls` refused the whole
     * revision for it, so a promotion the template could have honoured
     * failed entirely.
     *
     * A region is not a thing with a rank; it is where several ranked things
     * live. Ranking the leaves into the positions the containers leave over
     * reorders everything the promotion is about and moves no section.
     */
    const fixed = (c: TNode) => {
      const k = (c as unknown as { kind?: string }).kind;
      return k === 'capability' || k === 'region';
    };
    /* CONTIGUOUS RUNS, not one global pool — @Codex's P2 on `d881f5b`.
     *
     * The two paragraphs above say a module is *"not a thing a person can
     * promote past"* and that a container *"holds its index"*. It held its
     * INDEX and the content was ranked across it: every movable child went
     * into one list, sorted, and written back into the movable slots in
     * order — so a leaf after the chrome could take a slot before it.
     *
     * Reproduced through `reviseAccepted` before the fix, promoting `author`
     * on `[field(date), capability(search), field(author)]`:
     *
     *     ["f-author", "the-chrome", "f-date"]
     *
     * The capability kept index 1 and the two fields swapped around it, which
     * is exactly the topology the invariant forbids. Holding an index is not
     * the same as being a BOUNDARY, and only the second is what "cannot
     * promote past" means.
     *
     * So each run of movable siblings between fixed ones is ranked in
     * isolation. A run of one cannot move, which is the correct outcome for
     * `author` above: a promotion that names something already alone in its
     * segment reorders nothing, rather than reordering the page. */
    const rankOf = (c: TNode): number => {
      const role = roleOf(c);
      /*
       * ONE LEADING BAND UNDER `headingFirst`, NOT TWO — @Codex's P2 on
       * `552d25e`.
       *
       * Title took -3 and a visual -2, so the ranking did not merely put the
       * heading first: it REVERSED an authored cover-before-title page.
       * Their case, and it is an accepted arrangement rather than a
       * contrived one — `[media.hero, title, date, author]`, promote
       * `author`:
       *
       *   two bands   [title, media.hero, author, date]   the cover moved
       *   one band    [media.hero, title, author, date]   only what was asked
       *
       * The promotion was about `author`, and a promotion should move what
       * it names and disturb what it does not. Sharing the band lets the
       * sort's existing `|| (x.i - y.i)` keep whichever order the tree
       * arrived in, so a template that emits title-first keeps title first
       * and one that emits a cover keeps its cover — the ranking stops
       * IMPOSING a topology and starts preserving the authored one.
       *
       * `headingFirst` still earns its keep: without it a visual leads the
       * title outright (-2 against -1), which is right for a CARD, where the
       * picture is what you recognise the object by before you read it. On a
       * page you are already at the object, and identity and cover are not
       * in competition at all.
       */
      const r = role === undefined ? prominent.length
        : role === 'title' || role === 'subtitle' ? (headingFirst ? -3 : -1)
          : isVisualKind(ROLE_KIND[role]) ? (headingFirst ? -3 : -2)
            : lead([role])[0]!;
      return r;
    };
    const out = [...nodes];
    let run: { c: TNode; i: number }[] = [];
    const settle = (): void => {
      if (run.length === 0) return;
      const ranked = run.map(({ c, i }) => ({ c, r: rankOf(c), i }));
      ranked.sort((x, y) => (x.r - y.r) || (x.i - y.i));
      // Back into the slots this RUN's nodes came from, in their new order.
      run.forEach((e, k) => { out[e.i] = ranked[k]!.c; });
      run = [];
    };
    nodes.forEach((c, i) => { if (fixed(c)) settle(); else run.push({ c, i }); });
    settle();
    return out;
  };

  const n = node as unknown as Record<string, unknown>;
  const kind = n.kind as string;

  if ((kind === 'field' || kind === 'media')
    && suppressed.has((n.binding as { role: Role }).role)) return undefined;
  if (kind === 'node-slot' && suppressed.has(n.via as Role)) return undefined;

  const next: Record<string, unknown> = { ...n };

  if (kind === 'card' && Array.isArray(n.bindings)) {
    /**
     * Filtered off the CLONE, so nothing here can reach the accepted spec.
     *
     * `reviseAccepted` deep-copies before this runs, which is what makes the
     * plain filter safe. It was not always: the first draft sorted by
     * decorating the binding objects with a temporary key, and those objects
     * were the caller's — two mutations of the accepted spec per revision,
     * correct only because the second undid the first. `blobs.ts` learned the
     * same rule from a review this week: `readonly` is not a runtime guarantee.
     */
    const kept = (n.bindings as Array<{ role: Role }>)
      .filter((b) => !suppressed.has(b.role));
    if (prominent.length) {
      /**
       * A STABLE sort by prominence rank, so an unnamed role keeps its place
       * relative to its unnamed neighbours.
       *
       * Decorated on a separate array rather than on the bindings themselves.
       * The comparator returns 0 for two roles of equal rank rather than
       * falling through to some other order — one that broke ties by role name
       * would reorder bindings the instruction says nothing about, which is the
       * redesign this file exists to prevent, arriving through a tiebreak.
       */
      /**
       * IDENTITY AND MEDIA STAY AHEAD here too — @Codex, P2 on `2ffce98`:
       * revising `[title, date]` with a promoted date moved Date ahead of
       * the object's name, while fresh generation and `TastePriors`' own
       * sentence hold the opposite. Tiered only when a promotion actually
       * reaches these bindings, for the reason the children ranking gives:
       * an untouched card must not be reordered at all.
       */
      const touched = kept.some((b) => prominent.includes(b.role));
      const rank = (role: Role): number => !touched ? lead([role])[0]!
        : isVisualKind(ROLE_KIND[role]) ? -2
          : role === 'title' || role === 'subtitle' ? -1
            : lead([role])[0]!;
      const ranked = kept.map((b, i) => ({ b, r: rank(b.role), i }));
      ranked.sort((x, y) => (x.r - y.r) || (x.i - y.i));
      kept.length = 0;
      kept.push(...ranked.map((e) => e.b));
    }
    next.bindings = kept;
  }

  if (Array.isArray(n.children)) {
    const kept = (n.children as TNode[])
      .map((c) => applyNodeLocal(c, taste))
      .filter((c): c is TNode => c !== undefined);
    /**
     * Prominence leads among SIBLINGS too, not only among a card's bindings.
     *
     * @Codex, P2 on #184: *"When an accepted arrangement contains `field` or
     * `media` siblings directly under a region, this recursion preserves their
     * existing order because prominence is applied only to a card's
     * `bindings`. A person can therefore press Promote on such a rendered
     * field and receive a successful accepted-spec revision whose layout is
     * unchanged."*
     *
     * A revision that reports success and changes nothing is the worst of the
     * three possible answers — worse than refusing, which at least says so.
     *
     * Only a node that names ONE role of its own is ranked: `field` and
     * `media` through `binding.role`, `node-slot` through `via`. A card holds
     * several roles and has no single rank, and a region holds none, so both
     * sit at `prominent.length` and keep their relative order. That is the
     * same restraint the binding sort below states — *"one that broke ties by
     * role name would reorder bindings the instruction says nothing about"* —
     * applied one level up, where the nodes being moved are whole pieces of
     * somebody's arrangement rather than fields inside one card.
     *
     * Ranked through `lead` rather than through a second copy of its
     * arithmetic, so the two levels cannot disagree about what a rank is.
     */
    next.children = byProminence(kept, (n.purpose as string | undefined) === 'editor');
  }
  if (Array.isArray(n.items)) {
    /**
     * An item whose whole template is suppressed away is DROPPED, not kept.
     *
     * The first draft returned the original item when its spec vanished,
     * commented *"an item must keep a spec"* — true of the schema and false as
     * a resolution: it silently re-admitted every role the person had hidden,
     * inside the one node kind where a template is repeated per object. A
     * suppression is *"honoured absolutely — an explicit hide that keeps coming
     * back is the fastest way to lose a user."*
     *
     * The schema requires at least one item, so a collection whose LAST
     * template is suppressed away has nothing left to draw, and the caller sees
     * the same refusal as suppressing every role at the root.
     */
    const items = (n.items as Array<{ spec: TNode; when?: unknown }>)
      .map((item) => {
        const spec = applyNodeLocal(item.spec, taste);
        return spec ? { ...item, spec } : undefined;
      })
      .filter((item): item is { spec: TNode; when?: unknown } => item !== undefined);
    if (items.length === 0) return undefined;
    next.items = items;
  }
  /**
   * And a capability's SLOTS, which this pass had never entered.
   *
   * @Codex, P2 on `7788a4c`: *"When a legal capability contains rendered
   * `field`, `media`, or `node-slot` children inside its `slots` record,
   * `applyNodeLocal()` processes `children` and `items` but never visits those
   * slot arrays. Suppressing a role there therefore leaves the binding behind
   * and later turns an otherwise mechanical Hide into `AcceptedNoLongerLegal`;
   * promoting it can return a successful revision with unchanged layout."*
   *
   * The second half is the sharper one: the refusal at least SAYS something.
   * A promotion that leaves a slot untouched returns success and an unchanged
   * lens, so the person's gesture went nowhere with nothing to notice.
   *
   * `roleReferences` walks `slots` — that is why the suppressed-role refusal
   * below fires at all — so the two halves of this file disagreed about
   * whether a slot is part of the arrangement. It is, and it is now walked by
   * both.
   *
   * A slot that empties is dropped from the record rather than kept as an
   * empty array: an empty slot draws the capability's own placeholder, which is
   * not what somebody who hid its only contents asked for. A capability whose
   * every slot empties still survives — it is a module with its own reason to
   * exist, unlike a region whose only purpose was its children.
   */
  if (n.slots && typeof n.slots === 'object') {
    const slots: Record<string, TNode[]> = {};
    for (const [slot, kids] of Object.entries(n.slots as Record<string, TNode[]>)) {
      const kept = kids
        .map((c) => applyNodeLocal(c, taste))
        .filter((c): c is TNode => c !== undefined);
      // Ranked, not merely walked — the second half of the same finding.
      if (kept.length) slots[slot] = byProminence(kept);
    }
    next.slots = slots;
  }

  /**
   * A node the suppression emptied is REMOVED, and this is decided AFTER the
   * children pass rather than before it.
   *
   * The first draft asked the card whether it had children by reading
   * `n.children` — the originals — so a card whose every child was itself
   * suppressed away survived as a card with an empty `children` array. The same
   * question has to be asked of what came back, and it has to be asked of
   * regions too: an empty region draws an empty box, which is the thing a
   * person who hid its only contents did not ask for.
   *
   * The root emptying out is not silent — `reviseAccepted` turns it into the
   * refusal, because a lens with nothing in it is not a revision.
   */
  /**
   * Emptied BY THIS PASS, not merely empty.
   *
   * @Codex, P2 on #184: *"When an accepted arrangement already contains a
   * zero-child region or a zero-binding, childless card, any request with
   * `suppressedRoles` or `prominentRoles` reaches this cleanup and deletes
   * that node even if the instruction concerns a role elsewhere. Both empty
   * shapes are schema-valid, so promoting an unrelated field can silently
   * remove authored structure that the revision contract requires
   * preserving."*
   *
   * Right, and the paragraph above says why in its own words without noticing
   * it had: *"an empty region draws an empty box, which is the thing a person
   * who hid its only contents did not ask for."* Somebody who placed an empty
   * region — a spacer, a slot they mean to fill — asked for exactly that, and
   * `0045`'s rule is that absence of an instruction means preservation.
   *
   * So the same predicate is asked of the node as it ARRIVED, and a node that
   * was already empty is left alone. Written as one function rather than two
   * conditions so the before and after cannot drift.
   */
  const hollow = (node: Record<string, unknown>): boolean =>
    ((kind === 'card' && (node.bindings as unknown[] | undefined)?.length === 0)
      || (kind === 'region' && (node.children as unknown[] | undefined)?.length === 0))
    && !(node.children as unknown[] | undefined)?.length;
  if (hollow(next) && !hollow(n)) return undefined;

  return next as unknown as TNode;
}

/**
 * Revise the accepted spec under this request's live instructions.
 *
 * Everything not owned by a live instruction is carried across untouched, which
 * is the whole contract. The two governed halves:
 *
 * - the top-level visual fields each live instruction owns, written from the
 *   instruction;
 * - the node-local consequences of `suppressedRoles` and `prominentRoles`.
 *
 * `target.cardinality` is NOT revised: a cardinality change is a shape change,
 * and `illegalities` refuses on it rather than absorbing it here. Absorbing it
 * would mean rebuilding the tree, which is a redesign wearing a revision's
 * name.
 */
/**
 * Would promoting this role change THIS accepted arrangement?
 *
 * @Codex, P2 on #184: *"After direct sibling promotion was added, a kept
 * non-baseline layout can make promotion effective even when this shape-only
 * guard returns false. For example, `rankRoles()` already places `date` ahead
 * of other ordinary metadata, so `promotionChangesRanking(lastShape, 'date')`
 * can be false while an authored layout still places a direct date field after
 * a card … Determine whether promotion changes the current accepted spec
 * rather than an independently generated role ranking."*
 *
 * Exactly the counterpart of `promotionChangesRanking`, which asks the same
 * question of a baseline the person is no longer looking at. While an accepted
 * spec exists every suggestion is a REVISION of it, so the ranking that
 * decides the answer is not `rankRoles`' — it is what `applyNodeLocal` does to
 * the nodes they arranged.
 *
 * And it is answered by RUNNING the revision both ways, for the same reason
 * `promotionChangesRanking` runs the ranking both ways: a predicate that
 * reasoned about which node kinds are reordered would be a second statement of
 * `applyNodeLocal`'s rules, free to drift from them — and the drift is what
 * produced this finding in the first place, one file over.
 *
 * Structurally cloned first. `applyNodeLocal` builds new arrays and does not
 * write into what it is handed, but `reviseAccepted` copies before calling it
 * and a predicate that relied on that not mattering would be relying on an
 * implementation detail of the function it is measuring.
 */
export function promotionChangesAccepted(accepted: TLayoutSpec, role: Role): boolean {
  const root = structuredClone(accepted.root) as TNode;
  const before = applyNodeLocal(root, undefined);
  const after = applyNodeLocal(root, { prominentRoles: [role] } as TastePriors);
  return JSON.stringify(before) !== JSON.stringify(after);
}

export function reviseAccepted(req: GenerationRequest & { accepted: TLayoutSpec }): TLayoutSpec {
  /**
   * The INPUT boundary, first, because it is the provenance question.
   *
   * `0049`: *"An accepted input that the current product could not hold is not
   * an arrangement the revision path may preserve … It does not reach
   * transformation, generic generator repair, or a fresh-baseline fallback."*
   *
   * Before this ran, an accepted spec carrying a read-write binding with no
   * mutation reached `compile()`, whose repair is written for a GENERATOR's
   * fresh proposal — so it downgraded the binding to read-only and returned a
   * revision that had quietly lost a person's grant. Repair is correct there
   * and is silent data loss here; `0049` narrows it by provenance, which is
   * what this line and the one before `return` are.
   *
   * Ahead of `illegalities()` deliberately: that function asks whether the
   * arrangement fits THIS request, a question that presumes the arrangement is
   * a product state at all. Asking it of a spec the product could not hold
   * reports a transformed symptom, which is the alternative `0049` rejects.
   */
  const entry = validatorProblems(req.accepted, 'accepted-input');
  if (entry.length) throw new AcceptedNoLongerLegal(entry, 'accepted-input');

  const problems = illegalities(req.accepted, req);
  if (problems.length) throw new AcceptedNoLongerLegal(problems);

  const live = new Set(effectiveInstructions(req.taste));
  /**
   * A DEEP copy, not a spread.
   *
   * A spread shares `root` with the accepted spec, and the caller holds both:
   * `LensState.accepted.spec` is the arrangement this revision is supposed to
   * preserve, so any in-place edit to one would be an edit to the other. The
   * pipeline happens to `structuredClone` before repairing, which is the only
   * reason a shared root was survivable rather than a bug already — a guarantee
   * on somebody else's side of a boundary is not one this function may rely on.
   */
  const revised = structuredClone(req.accepted) as unknown as Record<string, unknown>;

  if (live.has('density')) revised.density = req.taste!.density;
  if (live.has('typography')) revised.typography = req.taste!.typography;
  if (live.has('palette')) revised.palette = req.taste!.palette;

  if (live.has('suppressedRoles') || live.has('prominentRoles')) {
    const root = applyNodeLocal(revised.root as TNode, req.taste);
    // A revision that removed the entire tree is not a revision. It can only
    // happen by suppressing every role the arrangement draws, and a person who
    // did that has asked for something this generator cannot express — so it is
    // the same refusal rather than an empty lens.
    if (!root) {
      throw new AcceptedNoLongerLegal(
        ['/root: suppressing these roles would leave the arrangement with nothing to draw']);
    }
    revised.root = root;
  }

  /**
   * And REFUSE anything the node-local pass could not remove.
   *
   * @Codex, P2 on `be5b9ec`: *"When an accepted capability channel, query
   * sort/filter/grouping, or view-state slot references a suppressed role, this
   * blanket exemption assumes the later revision will remove that reference,
   * but `applyNodeLocal()` only strips field/media nodes, card bindings, and
   * node slots. The resulting candidate can therefore keep drawing or querying
   * a role the person explicitly hid — and can even retain a reference to a
   * role absent from the current shape. Transform every applicable role-bearing
   * site, or refuse any suppressed reference that remains."*
   *
   * Refusing is the half taken, and deliberately: transforming a query's
   * `groupBy` means CHOOSING a replacement grouping, which is a redesign this
   * file exists to forbid, and dropping a sort silently changes an order the
   * person arranged. A refusal hands that choice back — `0045`'s own rule that
   * *"the caller may explicitly make a new generation request without
   * `accepted` after showing the refusal; the generator may not choose that
   * loss on the caller's behalf."*
   *
   * The exemption above stays, and the two together are the contract: don't
   * refuse a suppressed role while the pass is about to remove it, and do
   * refuse the one it could not.
   */
  const suppressed = new Set(req.taste?.suppressedRoles ?? []);
  if (suppressed.size) {
    const left = roleReferences(revised as unknown as TLayoutSpec)
      .filter(({ role }) => suppressed.has(role));
    if (left.length) {
      throw new AcceptedNoLongerLegal(left.map(({ role, path }) =>
        `${path}: "${role}" is hidden, and this arrangement binds it somewhere `
        + 'a revision cannot remove without redesigning'));
    }
  }

  /**
   * And the OUTPUT boundary, whose owner is different.
   *
   * `0049`: *"A legal accepted input can become illegal because a live revision
   * instruction was implemented incorrectly. That candidate is also refused
   * before it leaves `reviseAccepted()`. It must not flow into `compile()`'s
   * generic repair."*
   *
   * Neither check makes the other redundant, and the decision says why in one
   * sentence: *"The first establishes that preservation begins from a product
   * state; the second establishes that the revision operation kept it one."*
   * Entry cannot see a bug in the transform above; output cannot tell an
   * inherited illegality from a manufactured one, and would report the symptom
   * rather than the provenance.
   */
  const exit = validatorProblems(revised as unknown as TLayoutSpec, 'revised-output');
  if (exit.length) throw new AcceptedNoLongerLegal(exit, 'revised-output');

  return revised as unknown as TLayoutSpec;
}
